import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import '../styles/BlogPrimitives.css';

const BLOG_POST_CANONICAL_URL =
  'https://circumvent.io/blog/i-helped-create-the-problem-im-now-solving';
const BLOG_POST_PUBLISHED_AT = '2026-07-08';
const BLOG_POST_READ_TIME = '5 min read';
const BLOG_POST_AUTHOR = 'Michael Watts';
const FEATURE_IMAGE_SRC = '/images/blog/second-act-cloud-conformity-to-circumvent.jpg';
const BLOG_POST_OG_TITLE = "I helped create the problem I'm now solving | Circumvent";
const BLOG_POST_OG_DESCRIPTION =
  "Almost ten years ago I started Cloud Conformity. Last year I started Circumvent. The most interesting differences aren't the ones I expected.";
const BLOG_POST_OG_IMAGE_URL =
  'https://circumvent.io/images/blog/second-act-cloud-conformity-to-circumvent.jpg';

const BlogPostPage = () => (
  <>
    <Helmet>
      <title>{BLOG_POST_OG_TITLE}</title>
      <meta name="description" content={BLOG_POST_OG_DESCRIPTION} />
      <link rel="canonical" href={BLOG_POST_CANONICAL_URL} />
    </Helmet>
    <SeoSocialTags
      title={BLOG_POST_OG_TITLE}
      description={BLOG_POST_OG_DESCRIPTION}
      url={BLOG_POST_CANONICAL_URL}
      ogType="article"
      articlePublishedTime={`${BLOG_POST_PUBLISHED_AT}T00:00:00.000Z`}
      ogImageUrl={BLOG_POST_OG_IMAGE_URL}
    />

    <main className="page page--light blog-post-page">
      <section className="page-section page-section--white" aria-labelledby="post-heading">
        <article className="page-container blog-post">
          <div className="blog-post__header">
            <Link to="/blog" className="blog-crumb-link">
              <span aria-hidden="true">←</span> Blog
            </Link>
            <h1 id="post-heading" className="blog-post__title">
              I helped create the problem I&apos;m now solving
            </h1>
            <p className="blog-post__lead">
              Almost ten years ago I started Cloud Conformity. Last year I started Circumvent. The
              most interesting differences aren&apos;t the ones I expected.
            </p>

            <ul className="blog-meta-list blog-meta-list--header" aria-label="Post details">
              <li>Perspective</li>
              <li>
                <time dateTime={BLOG_POST_PUBLISHED_AT}>8 Jul 2026</time>
              </li>
              <li>{BLOG_POST_READ_TIME}</li>
              <li>By {BLOG_POST_AUTHOR}</li>
            </ul>
          </div>

          <div className="blog-post__layout">
            <div className="blog-post__content">
              <figure className="blog-post__feature-image blog-post__feature-image--bleed">
                <img
                  src={FEATURE_IMAGE_SRC}
                  alt="Illustration of the journey from Cloud Conformity's flood of detection findings to Circumvent's AI-powered reasoning."
                  width={1400}
                  height={781}
                  loading="eager"
                  decoding="async"
                />
              </figure>
              <p>
                Almost ten years ago I started a company called Cloud Conformity. Last year I started another one, Circumvent. On paper, they look like the same kind of company: both cloud security, both founded by me, both backed by Paladin. But building the second one has felt completely different, and the most interesting differences aren't the ones I expected.
              </p>
              <p>
                The biggest one is this: a lot of what we're solving at Circumvent is the consequence of what companies like Cloud Conformity got right.
              </p>

              <h2 id="what-we-built-the-first-time">What we built the first time</h2>
              <p>
                Cloud Conformity was, at its core, a detection company. We integrated with AWS, Azure and Google Cloud, and continuously checked customers' environments against a library of best practices, eventually more than 1000 of them. We told you when an S3 bucket was public, when a security group was too open, when something drifted out of compliance. We were good at it. We grew a loyal customer base, and in 2019 Trend Micro acquired us.
              </p>
              <p>
                I'm proud of that company. But here's the uncomfortable truth I've sat with since: we, and an entire generation of security tools alongside us, got really good at finding problems. Detection became cheap and abundant. Scanners multiplied. Every layer of the stack got its own tool, each one generating its own stream of findings.
              </p>
              <p>
                And we handed all of it to the same overloaded security engineers.
              </p>

              <h2 id="the-problem-moved">The problem moved</h2>
              <p>
                When detection is scarce, more detection is obviously good. When detection is everywhere, the bottleneck moves. The hard part is no longer finding issues. It's working out which of the millions of findings actually matter, who owns them, whether they're even exploitable in your specific environment, and what to do about them.
              </p>
              <p>
                That's not a detection problem. It's an investigation, prioritization and remediation problem. And it's still being done, by and large, by humans manually triaging alerts, chasing down owners in Slack, and arguing about severity scores that have no idea whether the affected system is internet-facing or holds customer data.
              </p>
              <p>
                The industry won the battle I was fighting in 2016. The prize was a much bigger, more human problem. That's the problem Circumvent exists to solve.
              </p>

              <h2 id="why-now-and-not-then">Why now, and not then</h2>
              <p>
                Here's the part that genuinely couldn't have worked the first time around.
              </p>
              <p>
                At Cloud Conformity, our intelligence lived in rules. A rule is a brilliant way to encode "this configuration is bad." It is a terrible way to encode "this finding doesn't matter because the service is internal, the data is non-sensitive, there's a compensating control, and the owner already has a ticket open." That kind of judgment is contextual, messy, and exactly what an experienced security engineer does in their head. You couldn't write it as a rule. You needed reasoning.
              </p>
              <p>
                The technology to do that reasoning at scale simply didn't exist a decade ago. Now it does. At Circumvent we're building what is effectively an AI-powered virtual security engineering team, agents that understand a customer's environment as deeply as one of their own engineers, investigate findings instead of forwarding them, prioritize based on real business context rather than raw severity, and increasingly assist with the remediation itself.
              </p>
              <p>
                The ambition I had in 2016 was basically "find everything." The ambition now is "behave like a great security engineer." That second ambition was science fiction the first time. It's an engineering roadmap today. That shift, from rules that flag to agents that reason, is the single biggest difference between the two companies.
              </p>

              <h2 id="whats-different-in-me">What&apos;s different in me</h2>
              <p>
                The technology shift is the headline, but it's not the thing I feel most day to day. That's the experience.
              </p>
              <p>
                The first time, I learned the shape of the journey by walking into every wall. The second time, I recognize the walls. I know which problems are existential and which just feel urgent. I know how much of early-stage company building is resisting good ideas so you can finish the important one. We say no to far more than we used to, and we move faster because of it.
              </p>
              <p>
                I also undervalued, the first time, how much of a company is the things you can't rebuild quickly. Trust with investors: Paladin backed Cloud Conformity and chose to back us again, which compresses an enormous amount of early friction. A reputation that helps the right people take a meeting, or join the team. A network of customers who'll tell you the truth. None of that shows up on a roadmap, and all of it is compounding interest you only start earning the first time around.
              </p>
              <p>
                And there's a quieter kind of confidence. The first company taught me that this market is real, that I can build a team that ships, and that an outcome is possible. Conviction is cheap when you're guessing and expensive when you're right. I'm not guessing this time.
              </p>

              <h2 id="the-honest-version">The honest version</h2>
              <p>
                I want to be careful not to make this sound tidy. Plenty is harder the second time. Expectations are higher, the AI space is loud and crowded, and "I did it before" guarantees nothing. Some lessons from Cloud Conformity actively mislead, because the technology and the problem have both moved underneath them.
              </p>
              <p>
                But the through-line I keep coming back to is this: the first company helped make detection abundant, and abundance created a new problem. I got a front-row seat to that problem for years. Now there's finally a technology capable of solving it, and a team I trust to build it.
              </p>
              <p>
                Getting to help create a problem and then come back to solve it properly is a rare second act. I don't take it for granted.
              </p>
              <p>
                If you're a security engineer drowning in findings, or a builder thinking about second acts of your own, I'd genuinely like to hear how this lands for you.
              </p>

              <Link to="/blog" className="blog-crumb-link">
                <span aria-hidden="true">←</span> Blog
              </Link>
            </div>

            <aside className="blog-post__sidebar" aria-label="Post navigation">
              <nav className="blog-toc" aria-label="On this page">
                <p className="blog-toc__title">On this page</p>
                <ul className="blog-toc__list">
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#what-we-built-the-first-time">
                      What we built the first time
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#the-problem-moved">
                      The problem moved
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#why-now-and-not-then">
                      Why now, and not then
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#whats-different-in-me">
                      What&apos;s different in me
                    </a>
                  </li>
                  <li className="blog-toc__item">
                    <a className="blog-toc__link" href="#the-honest-version">
                      The honest version
                    </a>
                  </li>
                </ul>
              </nav>
            </aside>
          </div>
        </article>
      </section>
    </main>
  </>
);

export default BlogPostPage;
